import { CircularProgress, Radio, Typography } from '@material-ui/core'

import { isolate } from '../../../../helpers/bidi'
import { getTranslate } from '../../../../localization'
import { AdvertisedPromotion } from '../../../../types/cart'
import useStyles from './styles/index.styles'
import { knownBlockerFor } from './viability'

/**
 * Percent discounts arrive as basis points (1700 = 17.00%), matching the promotion
 * service's own representation. Amount discounts arrive as minor units, which need a
 * currency to render, so they are shown as-is rather than guessed at here.
 */
export const describeDiscount = (promotion: AdvertisedPromotion): string => {
  if (promotion.discountType === 'Percent') {
    // isolate(): "%" after a Latin numeral inside Persian prose is the classic bidi
    // mirroring failure — it can render on the wrong side of the number.
    return isolate(`${Number(promotion.discountValue) / 100}%`)
  }
  return `${isolate(promotion.discountValue)} ${promotion.currency}`
}

const OFFER_GROUP_LABEL_ID = 'code-discount-group-label'

/** One selectable offer. */
const OfferRow = ({
  offer,
  selected,
  disabled,
  onSelect
}: {
  offer: AdvertisedPromotion
  selected: boolean
  disabled: boolean
  onSelect: (offer: AdvertisedPromotion) => void
}) => {
  const classes = useStyles()
  const blocked = knownBlockerFor(offer) === 'guest-identity'
  const describedBy = `offer-detail-${offer.promotionId}`

  return (
    <label className={classes.offerRow}>
      <Radio
        checked={selected}
        color="primary"
        disabled={disabled}
        name="code-discount"
        /*
         * NO aria-label here, deliberately.
         *
         * It used to carry `offer.name`, which OVERRIDES the wrapping <label> and makes the
         * name the entire accessible name — so the rate, the code, and the "applies once
         * guest details are entered" warning were announced to nobody. That warning is the
         * difference between an offer that will apply and one that will not.
         *
         * The <label> now supplies the name and aria-describedby carries the rest.
         */
        inputProps={{ 'aria-describedby': describedBy }}
        // onChange, not onClick. Re-clicking the selected radio used to CLEAR it — a
        // deselect gesture radios do not have, unreachable by keyboard arrows and
        // undiscoverable to everyone. Clearing is now an explicit option in the group.
        onChange={() => onSelect(offer)}
      />
      <span className={classes.offerText}>
        <Typography variant="body1">
          {offer.name}
          {/* The rate, pulled out of the sentence so a column of offers can be
              compared at a glance. It is a RATE, not this cart's saving — only
              evaluatePromotion knows the money, and that is shown once applied. */}
          <span className={classes.offerDiscount}>
            {describeDiscount(offer)}
          </span>
        </Typography>
        <span id={describedBy}>
          <Typography variant="body2" className={classes.helper}>
            {getTranslate('code {code}', { code: isolate(offer.code) })}
          </Typography>
          {blocked && (
            // Said on the offer itself rather than only after Continue. The advertised list
            // clears the per-guest cap so offers can be shown before a guest exists, which
            // means this one is listed and cannot apply yet — without a word here that reads
            // as the discount silently failing.
            <Typography variant="body2" className={classes.blocker}>
              {getTranslate(
                'Applies once guest details are entered — this offer is limited per guest.'
              )}
            </Typography>
          )}
        </span>
      </span>
    </label>
  )
}

type OfferListProps = {
  offers: AdvertisedPromotion[]
  selectedCode?: string
  disabled: boolean
  /** The offers lookup is still in flight. Only shown while there is nothing to list. */
  loading: boolean
  /** The lookup failed. An empty list must not be read as "no offers exist". */
  failed?: boolean
  onSelect: (offer: AdvertisedPromotion) => void
  onSelectNone: () => void
}

/**
 * Discounts the operator can give this guest by supplying a code.
 *
 * Named for the MECHANISM, not with an adjective. The previous heading was "Available
 * offers", directly beneath a checkbox reading "apply the best AVAILABLE offer
 * automatically" — the same word for two disjoint sets, which is a lexical claim that they
 * are one pool. They never were: this list is hard-gated to `code_entry` promotions
 * (`AdvertisementAllowed`) and the checkbox applied only `auto_applied` ones.
 *
 * The distinction cannot rest on "offer" versus "discount" either: Persian renders both as
 * تخفیف, and this panel is Persian-first, so an English near-synonym pair disambiguates
 * nothing for most of its users. Hence a phrase describing what the operator must DO.
 */
const OfferList = ({
  offers,
  selectedCode,
  disabled,
  loading,
  failed = false,
  onSelect,
  onSelectNone
}: OfferListProps) => {
  const classes = useStyles()

  if (offers.length === 0) {
    if (loading) {
      return (
        <div className={classes.offersLoading}>
          <CircularProgress size={18} />
        </div>
      )
    }
    if (failed) {
      // An empty list and a failed lookup look identical on screen, and mean opposite
      // things. Saying so keeps "none are advertised" from being inferred from a network
      // error — the code field below still works either way.
      return (
        <Typography variant="body2" className={classes.blocker}>
          {getTranslate(
            'Could not load the list of discounts. You can still enter a code.'
          )}
        </Typography>
      )
    }
    return null
  }

  return (
    <div
      className={classes.group}
      role="radiogroup"
      aria-labelledby={OFFER_GROUP_LABEL_ID}>
      <Typography
        variant="body2"
        className={classes.groupLabel}
        id={OFFER_GROUP_LABEL_ID}>
        {getTranslate('Discounts that need a code')}
      </Typography>
      <Typography variant="body2" className={classes.helper}>
        {getTranslate(
          'Choose one to offer this guest, or enter a code they already have.'
        )}
      </Typography>

      {offers.map((offer) => (
        <OfferRow
          key={offer.promotionId}
          offer={offer}
          selected={selectedCode === offer.code}
          disabled={disabled}
          onSelect={onSelect}
        />
      ))}

      {/* An explicit way back to "none", replacing the undiscoverable gesture of clicking
          the selected radio again. That gesture could not be reached with arrow keys, has no
          ARIA semantics, and nothing announced it. A row in the group is reachable, focusable
          and announced like any other. */}
      <label className={classes.offerRow}>
        <Radio
          checked={!selectedCode}
          color="primary"
          disabled={disabled}
          name="code-discount"
          onChange={onSelectNone}
        />
        <span className={classes.offerText}>
          <Typography variant="body1">
            {getTranslate('No code discount')}
          </Typography>
        </span>
      </label>
    </div>
  )
}

export default OfferList
